import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
} from "@mui/material";
import { CheckCircle, ArrowForward } from "@mui/icons-material";
import { monthlyPlans, annualPlans } from "../Constants/pricing";
import { PricingUrls } from "../Constants/pricingUrls";
import { monthlyPlansIndia, annualPlansIndia } from "../Constants/pricingIndia";
import { PricingIndiaUrls } from "../Constants/pricingIndiaUrls";
import PricingDetailsSections from "../Components/PricingDetailsSections";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import FAQSection from "../Components/PricingFaqSection";

const Pricing = ({ user, login, isLoggedIn, region = "us" }) => {
  const [isAnnual, setIsAnnual] = useState(true);
  const [planName, setPlanName] = useState("");
  const [accountType, setAccountType] = useState("");
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

 useEffect(() => {
  if (isLoggedIn) {
    const fetchUsageInfo = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/user-usage-info`
        );
        setPlanName(res.data?.subscription?.plan.toUpperCase());
        setAccountType(res.data?.subscription?.accountType?.toLowerCase());

        // Merge user and res.data.subscription for notification
        const mergedUserData = {
          ...user,
          ...(res.data || {})
        };

        // Call notification-email API with the merged data
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/notification/pricing-view`,
          {
            userData: mergedUserData
          }
        );
      } catch (err) {
        console.error("Failed to fetch user usage info or notify pricing view", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsageInfo();
  }
}, [isLoggedIn, user]);


  const handleSelectPlan = async (plan) => {
    if (!isLoggedIn) {
      return await login();
    }

    let currentPlan = planName;
    let currentAccountType = accountType;
    let mergedUserData = { ...user };
    if (!currentPlan) {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/user-usage-info`
        );
        currentPlan = res.data?.subscription?.plan?.toUpperCase();
        setPlanName(currentPlan);
        mergedUserData = { ...user, ...(res.data || {}) };
      } catch (err) {
        console.error("Failed to fetch user usage info after login", err);
      }
    }

    // Send email with mergedUserData and selected plan name
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/notification/pricing-view`,
        {
          userData: {
            ...mergedUserData,
            selectedPlan: plan.name,
            billingCycle: isAnnual ? "annual" : "monthly",
          },
        }
      );
    } catch (err) {
      console.error("Failed to send pricing view email on plan select", err);
    }

    if (currentAccountType && currentAccountType !== "free") {
      navigate("/subscription");
      return;
    }

    const billingCycle = isAnnual ? "annual" : "monthly";
    const key = `${plan.name.toLowerCase()}-${billingCycle}`;
    let url = region === "india" ? PricingIndiaUrls[key] : PricingUrls[key];
    url = url + "?prefilled_email=" + encodeURIComponent(user?.email || "");
    if (url) {
      window.location.href = url;
    }
  };

  const plans =
    region === "india"
      ? isAnnual
        ? annualPlansIndia
        : monthlyPlansIndia
      : isAnnual
      ? annualPlans
      : monthlyPlans;

  const visiblePlans = isMobile
    ? plans.filter((plan) => plan.name.toLowerCase() !== "free")
    : plans;

  if (loading || (isLoggedIn && !planName)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        width="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: 2.5,
        background: theme.palette.background.paper,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 800,
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "inline-block",
            }}
          >
            Simple, Transparent Pricing
          </Typography>
          {accountType === "paid" && (
            <Box my={1}>
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Typography variant="body1">
                  You are currently on the <strong>{planName}</strong> plan.
                </Typography>
              </Alert>
            </Box>
          )}

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 4 }}
          >
            <Typography variant="body1" sx={{ mr: 1 }}>
              Monthly
            </Typography>
            <Switch
              checked={isAnnual}
              onChange={() => setIsAnnual(!isAnnual)}
              color="primary"
            />
            <Typography variant="body1" sx={{ ml: 1, fontWeight: 600 }}>
              Annual{" "}
              <Chip
                label="50% Off Now"
                size="small"
                color="success"
                sx={{ ml: 1 }}
              />
            </Typography>
          </Box>
        </Box>

        <Grid
          container
          spacing={3}
          justifyContent="center"
          sx={{
            px: { xs: 2, sm: 3, md: 6 },
            [theme.breakpoints.up("lg")]: {
              flexWrap: "nowrap",
            },
          }}
        >
          {visiblePlans.map((plan) => {
            const isFree = plan.name.toLowerCase() === "free";
            const hasComparePrice = isAnnual && plan.comparePrice;

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={true}
                key={plan.name}
                sx={{
                  minWidth: 220,
                  maxWidth: 260,
                  [theme.breakpoints.up("lg")]: {
                    flex: "1 1 0px",
                  },
                }}
              >
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: plan.popular
                      ? `2px solid ${theme.palette.primary.main}`
                      : "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 3.6,
                    transition: "all 0.3s ease",
                    position: "relative",
                    backgroundColor: "#fff",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      background: plan.popular
                        ? `linear-gradient(180deg, ${theme.palette.primary.light}30 0%, ${theme.palette.primary.main}10 100%)`
                        : `linear-gradient(180deg, ${theme.palette.action.hover} 0%, #f0f0f0 100%)`,
                      boxShadow: plan.popular
                        ? `0 8px 24px ${theme.palette.primary.main}66`
                        : `0 6px 20px rgba(0, 0, 0, 0.15)`,
                    },
                    ...(plan.popular && {
                      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
                    }),
                  }}
                >
                  {plan.popular && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 40,
                        bgcolor: "primary.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                        fontWeight: 900,
                        fontSize: 16,
                        zIndex: 1,
                      }}
                    >
                      MOST POPULAR
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, px: 2, py: 3 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      gutterBottom
                      sx={{ mt: 2 }}
                    >
                      {plan.name}
                    </Typography>

                    <Box sx={{ my: 2 }}>
                      {isAnnual ? (
                        <>
                          <Box sx={{ display: "flex", alignItems: "baseline" }}>
                            {hasComparePrice && (
                              <Typography
                                sx={{
                                  color: "text.secondary",
                                  textDecoration: "line-through",
                                  mr: 1,
                                }}
                              >
                                {plan.comparePrice}
                              </Typography>
                            )}
                            <Typography
                              variant="h4"
                              component="div"
                              sx={{ fontWeight: 800 }}
                            >
                              {plan.price}
                            </Typography>
                          </Box>
                          <Typography color="text.secondary">
                            per month
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="h4"
                            component="div"
                            sx={{ fontWeight: 800 }}
                          >
                            {plan.price}
                          </Typography>
                          <Typography color="text.secondary">
                            per month
                          </Typography>
                        </>
                      )}
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <Button
                        variant={plan.popular ? "contained" : "outlined"}
                        color="primary"
                        fullWidth
                        size="large"
                        onClick={() => handleSelectPlan(plan)}
                        disabled={isFree}
                        sx={{
                          py: 1.5,
                          fontWeight: 600,
                          ...(plan.popular && {
                            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                          }),
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : accountType === "paid" ? (
                          "Manage Plan"
                        ) : (
                          plan.cta
                        )}
                      </Button>
                    </Box>

                    <List sx={{ textAlign: "left" }}>
                      {plan.features.map((feature, index) => (
                        <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircle color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Need help choosing a plan?
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="/contact"
            endIcon={<ArrowForward />}
            sx={{
              px: 6,
              py: 1.5,
              fontWeight: 700,
              fontSize: "1rem",
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              boxShadow: `0 4px 15px ${theme.palette.primary.main}30`,
            }}
          >
            Contact Our Team
          </Button>
          <PricingDetailsSections />
          <FAQSection />
        </Box>
      </Container>
    </Box>
  );
};

export default Pricing;
